<template>
  <Dialog v-model:visible="dialogVisible" modal
      class="mx-2 sm:mx-4 black-bg travel-buddy-feedback-dialog"
      :style="{ width: '50rem' }">
    <template #header>
      <div class="inline-flex items-center justify-center gap-2 flex-wrap text-center">
          <font-awesome-icon class="text-xl sm:text-2xl text-[var(--color-orange)] shrink-0"
              icon="fa-regular fa-lightbulb"/>
          <span class="font-bold text-base sm:text-xl break-words">
              {{ $t('TravelBuddyFeedbackOverlayComponent.yourFeedback') }}
          </span>
      </div>
    </template>

    <form action="#" class="px-1" @submit.prevent="submitFeedback">
      <div class="flex flex-col gap-2">
        <p class="w-full pt-2 font-medium">
          {{ $t('TravelBuddyFeedbackOverlayComponent.meetingStatusQuestion') }}
        </p>
        <p v-if="isEditOnlyMode" class="text-[var(--color-orange)] font-medium">
          {{ meetingStatusSelected === 'completed'
            ? $t('TravelBuddyFeedbackOverlayComponent.completed')
            : $t('TravelBuddyFeedbackOverlayComponent.skip') }}
        </p>
        <div v-else class="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            :class="meetingStatusSelected === 'completed' ? 'btn-primary-accent' : 'btn-secondary'"
            @click="$store.commit('setTravelBuddyMeetingStatus', 'completed')"
            unstyled
          >
            {{ $t('TravelBuddyFeedbackOverlayComponent.completed') }}
          </Button>
          <Button
            type="button"
            :class="[
              meetingStatusSelected === 'skipped' ? 'btn-primary-accent' : 'btn-secondary',
              isSkipDisabled ? 'opacity-50 cursor-not-allowed' : '',
            ]"
            :disabled="isSkipDisabled"
            @click="!isSkipDisabled && $store.commit('setTravelBuddyMeetingStatus', 'skipped')"
            unstyled
          >
            {{ $t('TravelBuddyFeedbackOverlayComponent.skip') }}
          </Button>
        </div>
        <small v-if="isSkipDisabled && !isEditOnlyMode" class="text-[var(--color-disabled)]">
          {{ $t('TravelBuddyFeedbackOverlayComponent.skipDisabledWhenCompletedHint') }}
        </small>
      </div>

      <div v-if="meetingStatusSelected === 'completed'"
        class="flex flex-col gap-2 justify-start mt-4 travel-buddy-rating-wrap">
        <p class="w-full font-medium">
          {{ $t('TravelBuddyFeedbackOverlayComponent.helpfulnessQuestion') }}
        </p>
        <Rating
          v-model="helpfulnessScore"
          :cancel="false"
          :readonly="isEditOnlyMode ?
            (meetingStatusSelected === 'skipped') : (meetingStatusSelected !== 'completed')"
          :disabled="meetingStatusSelected !== 'completed'"
        />
        <small class="text-[var(--color-disabled)]">
          {{ $t('TravelBuddyFeedbackOverlayComponent.helpfulnessSkippedHint') }}
        </small>
      </div>

      <div class="flex flex-col gap-2 justify-start mt-4">
        <p class="w-full whitespace-pre-line">{{ dynamicMeetingFeedbackQuestion }}</p>
        <Textarea
          v-model="feedbackText"
          required
          maxlength="5000"
          class="text-base h-28 w-full"
          :placeholder="$t('TravelBuddyFeedbackOverlayComponent.feedbackPlaceholder')"
        />
      </div>

      <div v-if="meetingStatusSelected === 'completed'" class="flex flex-col gap-2 justify-start mt-4">
        <p class="w-full">
          {{ $t('TravelBuddyFeedbackOverlayComponent.furtherThoughtsQuestion') }}
        </p>
        <Textarea
          v-model="furtherThoughts"
          maxlength="5000"
          class="text-base h-28 w-full"
          :placeholder="$t('TravelBuddyFeedbackOverlayComponent.furtherThoughtsPlaceholder')"
        />
      </div>

      <div v-if="meetingStatusSelected === 'completed'" class="mt-4">
        <div class="flex items-center gap-2">
          <Checkbox
            v-model="shareWithTravelBuddy"
            input-id="shareWithTravelBuddy"
            :binary="true"
            :disabled="meetingStatusSelected !== 'completed'"
          />
          <label for="shareWithTravelBuddy">
            {{ $t('TravelBuddyFeedbackOverlayComponent.shareWithTravelBuddyLabel', {text: featureName}) }}
          </label>
        </div>
        <small class="text-[var(--color-disabled)]">
          {{ $t('TravelBuddyFeedbackOverlayComponent.shareWithTravelBuddyNote', {text: featureName}) }}
        </small>
      </div>

      <div class="flex justify-end mt-4">
        <Button
          type="submit"
          class="btn-primary-accent px-6"
          :disabled="isSubmitDisabled"
          unstyled
        >
          {{ $t('TravelBuddyFeedbackOverlayComponent.finish') }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>

<script>
import {
  Button,
  Checkbox,
  Dialog,
  Rating,
  Textarea,
} from 'primevue';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

export default {
  name: 'TravelBuddyFeedbackOverlayComponent',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    Button,
    Dialog,
    Textarea,
    Checkbox,
    Rating,
  },
  data() {
    return {
      helpfulnessScore: null,
      feedbackText: '',
      furtherThoughts: '',
      shareWithTravelBuddy: false,
      buttonDisabled: false,
    };
  },
  watch: {
    dialogVisible: {
      handler(newValue) {
        if (newValue) {
          this.prefillFromMeeting();
        }
      },
      immediate: true,
    },
  },
  methods: {
    prefillFromMeeting() {
      const currentMeeting = this.$store.getters.getTravelBuddyMeetingInfo?.meetingInfo;
      const teammateId = this.$store.getters.getTravelBuddyMeetingInfo?.teammateID;
      const ownFeedbackEntry = currentMeeting?.meetingFeedback?.find(
        (entry) => String(entry.user) !== String(teammateId),
      );

      this.helpfulnessScore = ownFeedbackEntry?.helpfulnessScore ?? null;
      this.feedbackText = ownFeedbackEntry?.feedback || '';
      this.furtherThoughts = ownFeedbackEntry?.furtherThoughts || '';
      this.shareWithTravelBuddy = Boolean(ownFeedbackEntry?.shareWithTravelBuddy);

      // Prefill store status from meeting; completed locks skip, skipped can upgrade to completed
      const status = String(currentMeeting?.meetingStatus || '');
      if (status === 'completed' || status === 'skipped') {
        this.$store.commit('setTravelBuddyMeetingStatus', status);
      }
    },
    async submitFeedback() {
      const meetingInfo = this.$store.getters.getTravelBuddyMeetingInfo;
      const meetingId = meetingInfo?.meetingInfo?._id;

      if (!meetingId) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('TravelBuddyFeedbackOverlayComponent.errorCouldNotFeedback'),
          detail: '',
          life: 3000,
        });
        return;
      }

      try {
        this.buttonDisabled = true;

        const isCompleted = this.meetingStatusSelected === 'completed';
        await this.$store.dispatch('submitTravelBuddyFeedback', {
          meetingDate: new Date().toISOString(),
          meetingID: meetingId,
          meetingStatus: this.meetingStatusSelected,
          helpfulnessScore: isCompleted ? this.helpfulnessScore : null,
          feedbackText: this.feedbackText.trim(),
          furtherThoughts: isCompleted ? this.furtherThoughts.trim() : '',
          shareWithTravelBuddy: isCompleted ? this.shareWithTravelBuddy : false,
          editOnly: this.isEditOnlyMode,
        });

        this.$toast.add({
          severity: 'success',
          summary: this.$t('TravelBuddyFeedbackOverlayComponent.feedbackSent'),
          detail: '',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('TravelBuddyFeedbackOverlayComponent.errorCouldNotFeedback'),
          detail: '',
          life: 3000,
        });
      } finally {
        this.buttonDisabled = false;
        this.$emit('update:visible', false);
        this.$emit('close-parent');
      }
    },
  },
  computed: {
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'travelBuddyHistory',
        this.$t('FallbackTexts.travelBuddyHistory'),
        this.$i18n?.locale,
      );
    },
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
        if (!value) {
          this.$emit('close-parent');
        }
      },
    },
    /** Meeting already completed — Skip must stay disabled (API also rejects completed→skipped). */
    isSkipDisabled() {
      const status = String(
        this.$store.getters.getTravelBuddyMeetingInfo?.meetingInfo?.meetingStatus || '',
      );
      return status === 'completed';
    },
    isEditOnlyMode() {
      return this.$store.getters.getTravelBuddyEditFeedbackMode;
    },
    meetingStatusSelected() {
      if (this.isEditOnlyMode) {
        return this.$store.getters.getTravelBuddyMeetingInfo?.meetingInfo?.meetingStatus
          ?? this.$store.getters.getTravelBuddyMeetingStatus;
      }
      // If meeting is completed, force completed selection even if store was set to skipped
      if (this.isSkipDisabled) {
        return 'completed';
      }
      return this.$store.getters.getTravelBuddyMeetingStatus;
    },
    dynamicMeetingFeedbackQuestion() {
      if (this.meetingStatusSelected === 'skipped') {
        return this.$t('TravelBuddyFeedbackOverlayComponent.howDoYouFeelSkip');
      }
      return this.$t('TravelBuddyFeedbackOverlayComponent.howDoYouFeel');
    },
    isSubmitDisabled() {
      const hasRequiredFeedbackText = Boolean(this.feedbackText.trim());
      const requiresHelpfulness = this.meetingStatusSelected === 'completed' && !this.isEditOnlyMode;
      const missingHelpfulness = requiresHelpfulness && !this.helpfulnessScore;
      return this.buttonDisabled || missingHelpfulness || !hasRequiredFeedbackText;
    },
  },
};
</script>
<style scoped>
.travel-buddy-rating-wrap :deep(.p-rating-icon) {
  color: var(--color-orange);
  height: 1.25rem !important;
  width: 1.25rem !important;
}

.travel-buddy-rating-wrap :deep(.p-rating-icon.p-rating-icon-active) {
  color: var(--color-orange);
  height: 1.25rem !important;
  width: 1.25rem !important;
}

.travel-buddy-rating-wrap :deep(.p-rating-icon:hover),
.travel-buddy-rating-wrap :deep(.p-rating-item:hover .p-rating-icon:hover) {
  color: var(--color-orange) !important;
  height: 1.25rem !important;
  width: 1.25rem !important;
}

@media (max-width: 640px) {
  :deep(.p-dialog) {
    width: calc(100vw - 1rem) !important;
    max-width: calc(100vw - 1rem);
  }
}
</style>
